//@flow
import React, {useMemo, useState} from 'react';
import {makeStyles, Typography, Divider} from '@material-ui/core';
import {Description, Info, Close} from '@material-ui/icons';

import {colors} from '../../../assets/styles/colors';
import {formatDateTime} from '../../../utils/date';
import type {
  ProductBatchLoadTask,
  ProductBatchLoadResult,
} from '../../../entities';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    flexDirection: 'column',
    padding: '.5rem',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
  },
  resultInformation: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    width: '100%',
  },
  divider: {
    width: '100%',
  },
  preview: {
    width: '3rem',
    height: '3rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '.5rem',
    background: colors.grey300,
  },
  name: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  description: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
  resultStatus: {
    width: '100%',
  },
  resultDone: {
    padding: '.5rem',
    background: `${colors.success}10`,
    borderRadius: '.5rem',
    color: colors.success,
    fontSize: '.875rem',
    width: '100%',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  resultFailed: {
    padding: '.5rem',
    background: colors.grey100,
    borderRadius: '.5rem',
    color: colors.grey700,
    fontSize: '.875rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  resultFailedMessage: {
    fontWeight: 500,
    fontSize: '.875rem',
  },
  resultFailedDescription: {
    fontSize: '.875rem',
    color: colors.grey700,
  },

  resultError: {
    padding: '.5rem',
    background: `${colors.error}10`,
    borderRadius: '.5rem',
    color: colors.error,
    fontSize: '.875rem',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 500,
    display: 'flex',
    gap: '.5rem',
  },
  resultErrorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
  },
  closeButtonError: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.75rem',
    height: '1.75rem',
    borderRadius: '1.5rem',
    background: colors.error,
    color: 'white',
    cursor: 'pointer',
    border: 'none',
  },
  closeButtonSuccess: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.75rem',
    height: '1.75rem',
    borderRadius: '1.5rem',
    background: colors.success,
    color: 'white',
    cursor: 'pointer',
    border: 'none',
  },
}));

type Props = {
  batchTask: ProductBatchLoadTask,
};

const MAP_SHEET_PAGE_NAME = {
  PRODUCTS: 'Hoja de Productos',
  PRODUCT_VARIANTS: 'Hoja de Variantes',
  BULK_PRICES: 'Hoja de Precios al por mayor',
};
function getErrorMessage(errorName: string) {
  if (errorName === 'FileTooLargeError') {
    return 'El número de filas excede el límite permitido';
  }

  if (errorName === 'DocumentValidationError') {
    return 'Hay un error en el documento, porfavor revisar los datos de la plantilla';
  }

  return 'Error desconocido';
}

const getErrorTypeLabel = (errorType: string) => {
  if (errorType === 'not_found') return 'Valor no encontrado';
  if (errorType === 'type') return 'Error en valor';
  if (errorType === 'missing') return 'Campo vacío';
  if (errorType === 'value') return 'Valor incorrecto';
  if (errorType === 'another_page') return 'Error en otra hoja';

  return 'Error indeterminado';
};

export default function BatchUploadResult(props: Props) {
  const {batchTask} = props;
  const classes = useStyles();

  const [showMessage, setShowMessage] = useState(true);

  const errorLines: ProductBatchLoadResult[] = useMemo(() => {
    const results = Array.isArray(batchTask.result) ? batchTask.result : null;
    if (!results) return [];

    return results.filter(line => line.message === 'error');
  }, [batchTask]);

  const errorLinesGrouped: {
    [key: string]: ProductBatchLoadResult[],
  } = useMemo(() => {
    const draft = {
      PRODUCTS: [],
      PRODUCT_VARIANTS: [],
      BULK_PRICES: [],
    };

    for (const key in draft) {
      const errors = errorLines.filter(
        line => line.scope?.toUpperCase() === key
      );

      if (errors.length) draft[key] = errors;
      else delete draft[key];
    }

    return draft;
  }, [errorLines]);

  return (
    <section className={classes.root}>
      <div className={classes.resultInformation}>
        <span className={classes.preview}>
          <Description />
        </span>

        <div className={classes.information}>
          <Typography className={classes.name}>Plantilla Productos</Typography>

          <Typography className={classes.description}>
            Fecha: {formatDateTime(new Date(batchTask.updatedAt))}
          </Typography>
        </div>
      </div>

      <Divider className={classes.divider} />

      <footer className={classes.resultStatus}>
        {batchTask.state === 'DONE' && (
          <div className={classes.resultDone}>
            <span>
              {errorLines.length === 0
                ? 'Su documento se subió correctamente'
                : 'Su documento se subió pero algunas filas presentaron errores'}
            </span>
            <button
              className={classes.closeButtonSuccess}
              onClick={() => {
                setShowMessage(false);
              }}
            >
              <Close fontSize="small" />
            </button>
          </div>
        )}

        {batchTask.state === 'FAILED' && showMessage && (
          <div className={classes.resultError}>
            <div className={classes.resultErrorTitle}>
              <Info fontSize="small" />
              <span>
                Hubo un error, no se logró subir el documento <br />
                {getErrorMessage(
                  // $FlowFixMe
                  batchTask.result?.errorName || 'Error desconocido'
                )}
              </span>
            </div>
            <button
              className={classes.closeButtonError}
              onClick={() => {
                setShowMessage(false);
              }}
            >
              <Close fontSize="small" />
            </button>
          </div>
        )}

        {errorLines.length > 0 && showMessage && (
          <div className={classes.resultFailed}>
            <Typography className={classes.resultFailedMessage}>
              Revisa los siguientes errores
            </Typography>
            {Object.entries(errorLinesGrouped).map(([key, value], i) => {
              const sheetName = MAP_SHEET_PAGE_NAME[key];
              // $FlowFixMe
              const errorLines: ProductBatchLoadResult[] = value;
              return (
                <div key={`error-line-${i}`}>
                  <Typography
                    className={classes.resultFailedMessage}
                    gutterBottom
                    style={{fontWeight: 600}}
                  >
                    {sheetName}
                  </Typography>
                  {errorLines.map(line => (
                    <Typography
                      className={classes.resultFailedMessage}
                      key={line.field}
                    >
                      {`Fila ${line.index + 5} : Columna ${line.field ||
                        '?'} - ${
                        line.type
                          ? getErrorTypeLabel(line.type)
                          : 'Error indeterminado'
                      }`}
                    </Typography>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </footer>
    </section>
  );
}
